import React, {MutableRefObject, useCallback, useEffect, useMemo, useRef} from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';

import './Home.scss';
import { Calendar, EventEdgeStyle, ICalendarEvent, ICalendarEventPart, ICalendarRenderProps, ISidebarRouteState } from 'components';
import { useLuxonTimezone } from 'hooks';
import { IStage, IUpdateWithFile } from 'interfaces';
import { LocalStorageService, UpdatesService, UsersService } from 'services';
import { renderAgenda } from './CalendarComponents/renderAgenda';
import { renderEvent } from './CalendarComponents/renderEvent';
import { renderDayPanelEvent } from './CalendarComponents/renderDayPanelEvent';
import { useTranslation } from 'react-i18next';
import { formatDateDay, parseDateDay } from "services/Utils";

export interface IHomeCalendarData {
    stage: IStage,
    stageIndex: number,
    update: IUpdateWithFile,
}

const urlKeyRangeStart = 'start';
const urlKeyRangeEnd = 'end';

function formatURL(startDateStr: string, endDateStr: string): string {
    const params = new URLSearchParams();
    params.set(urlKeyRangeStart, startDateStr);
    params.set(urlKeyRangeEnd, endDateStr);
    return `/?${params.toString()}`;
}

export function Home() {
    const history = useHistory();
    const location = useLocation();
    const { timezone } = useLuxonTimezone(UsersService.me.timezone);
    const { t } = useTranslation();

    useEffect(() => {
        return () => UpdatesService.newUpdateFirstStageStartDate = null
    }, [])

    // Read dates from url or use defaults, using a memo to prevent resetting the dates on every render,
    // which will cause the calendar to rerender.
    // The url is our source of truth for dates so that browser back/next buttons will work.
    const urlParams = new URLSearchParams(location.search);
    const urlDateRangeStart = urlParams.get(urlKeyRangeStart);
    const urlDateRangeEnd = urlParams.get(urlKeyRangeEnd);

    const [startDate, endDate] = useMemo(() => {
        const now = DateTime.now().setZone(timezone);
        let startDt = now.startOf('month');
        // Default to showing 6 months of events. This end date is only used by the agenda view.
        let endDt = now.plus({months: 6}).endOf('month');

        if (urlDateRangeStart && urlDateRangeEnd) {
            const [newStart, newStartOk] = parseDateDay(urlDateRangeStart, timezone);
            const [newEnd, newEndOk] = parseDateDay(urlDateRangeEnd, timezone);

            if (newStartOk && newEndOk) {
                startDt = DateTime.fromJSDate(newStart, {zone: timezone});
                endDt = DateTime.fromJSDate(newEnd, {zone: timezone});
            } else {
                // If the dates aren't valid, replace the url with the default since we're using the default dates.
                // We're using window.history to avoid triggering a rerender
                window.history.replaceState(undefined, document.title, '/');
            }
        }
        return [startDt.toJSDate(), endDt.toJSDate()];
    }, [urlDateRangeStart, urlDateRangeEnd, timezone]);

    function onNavigate(newStart: Date, newEnd: Date) {
        saveOptionsInURL(newStart, newEnd);
    }

    function saveOptionsInURL(start: Date, end: Date) {
        const url = formatURL(formatDateDay(start, timezone), formatDateDay(end, timezone));
        history.push(url);
    }

    // Store the calendar's view in localstorage.
    function onChangeView(newView: string) {
        localStorage[LocalStorageService.KeyHomeCalendarView] = newView;
    }

    // On the Update page, if the user clicks the breadcrumb to come back here, we want to keep our url params.
    const sidebarRouteStateRef = useRef<ISidebarRouteState>();
    const startDateStr = formatDateDay(startDate, timezone);
    const endDateStr = formatDateDay(endDate, timezone);
    sidebarRouteStateRef.current = {
        returnURL: formatURL(startDateStr, endDateStr),
    };

    const renderAgendaCallback = useCallback((events: ICalendarEvent<IHomeCalendarData>[]) => {
        return renderAgenda(events, sidebarRouteStateRef.current);
    }, []);

    const renderEventCallback = useCallback((
        props: ICalendarRenderProps,
        event: ICalendarEvent<IHomeCalendarData>,
        eventPart: ICalendarEventPart<IHomeCalendarData>,
    ) => {
        return renderEvent(props, event, eventPart, sidebarRouteStateRef.current, timezone, t);
    }, [timezone]);

    const renderDayPanelEventCallback = useCallback((
        props: ICalendarRenderProps,
        event: ICalendarEvent<IHomeCalendarData>,
        eventPart: ICalendarEventPart<IHomeCalendarData>,
    ) => {
        return renderDayPanelEvent(props, event, eventPart, sidebarRouteStateRef.current, timezone);
    }, [timezone]);

    const loadCallback = useCallback((start: Date, end: Date) => {
        return UpdatesService.getUpdates(start, end).then((updates) => {
            const events: ICalendarEvent<IHomeCalendarData>[] = [];
            // Turn each stage into an event.
            for (const u of updates) {
                for (let stageI = 0; stageI < u.rollout.stages.length; stageI++) {
                    const s = u.rollout.stages[stageI];

                    events.push({
                        id: 'update_' + u.id + '_stageIndex_' + stageI,
                        start: s.startTime,
                        end: s.stopTime,
                        // Group by update so that the stages are rendered together.
                        groupId: u.id + '',
                        data: {
                            stage: s,
                            stageIndex: stageI,
                            update: u,
                        }
                    })
                }
            }
            return events;
        });
    }, []);

    return (
        <div className="homeContainer">
            <div className="home_content">
                <Calendar<IHomeCalendarData>
                    defaultEventEdgeStyle={EventEdgeStyle.Percent}
                    defaultView={localStorage[LocalStorageService.KeyHomeCalendarView]}
                    dateStart={startDate}
                    dateEnd={endDate}
                    onChangeSelectedDate={(date) => {
                        UpdatesService.newUpdateFirstStageStartDate = date
                    }}
                    onChangeView={onChangeView}
                    onNavigate={onNavigate}
                    onLoadEvents={loadCallback}
                    renderAgenda={renderAgendaCallback}
                    renderEvent={renderEventCallback}
                    renderEventDayPanel={renderDayPanelEventCallback}
                    timezone={timezone}
                />
            </div>
        </div>
    );
}